import * as React from "react";
import { cn } from "@/lib/utils";

export interface ButtonProps
    extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: "default" | "destructive" | "outline" | "secondary" | "ghost" | "link";
    size?: "default" | "sm" | "lg" | "icon";
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
    ({ className, variant = "default", size = "default", ...props }, ref) => {
        const variants = {
            default: "bg-neutral-900 text-neutral-50 hover:bg-neutral-900/90 shadow",
            destructive: "bg-red-500 text-neutral-50 hover:bg-red-500/90 shadow-sm",
            outline: "border border-neutral-200 bg-white hover:bg-neutral-100 hover:text-neutral-900 shadow-sm",
            secondary: "bg-neutral-100 text-neutral-900 hover:bg-neutral-100/80 shadow-sm",
            ghost: "hover:bg-neutral-100 hover:text-neutral-900",
            link: "text-neutral-900 underline-offset-4 hover:underline",
        };

        const sizes = {
            default: "h-9 px-4 py-2",
            sm: "h-8 rounded-md px-3 text-xs",
            lg: "h-10 rounded-md px-8",
            icon: "h-9 w-9",
        };

        return (
            <button
                className={cn(
                    "inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-neutral-950 disabled:pointer-events-none disabled:opacity-50",
                    variants[variant],
                    sizes[size],
                    className
                )}
                ref={ref}
                {...props}
            />
        );
    }
);
Button.displayName = "Button";

export { Button };
